import React, { useRef, useState, useEffect } from 'react';
import { Layout, Spin, message } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import TopBar from './Topbar';
import Sidebar from './Sidebar';
import DeviceMapPanel from './DeviceMapPanel';
import PlumeMapPanel from './PlumeMapPanel';
import ComplaintMapPanel from './ComplaintMapPanel';
import IncidentMapPanel from './IncidentMapPanel';
import useComplaints from '../hooks/useComplaints';
import useIncidents from '../hooks/useIncidents';
import { complaintsService } from '../services/complaintsService';
import { incidentsService } from '../services/incidentsService';
import { useSession } from '../context/SessionContext';
import { getActiveView } from '../utils/getActiveView';
import { usePermissions } from '../hooks/usePermissions';
import { useGlobalLoading } from '../context/LoadingContext';

const { Header } = Layout;

function Dashboard() {
  const location = useLocation();
  const mapRef = useRef();
  const navigate = useNavigate();
  const { hasRole } = useSession();
  const {
    canViewSidebar,
    canAccessHistoricalTab
  } = usePermissions();
  const { startLoading, stopLoading } = useGlobalLoading();

  const [pendingFlyTo, setPendingFlyTo] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceInfoOpen, setDeviceInfoOpen] = useState(false);
  const [complaintInfoOpen, setComplaintInfoOpen] = useState(false);
  const [openPanels, setOpenPanels] = useState([]);
  const [mapCenter, setMapCenter] = useState(null);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [indicentInfoOpen, setIncidentInfoOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  const [plumePanelVisible, setPlumePanelVisible] = useState(false);
  const [plumePanelCollapsed, setPlumePanelCollapsed] = useState(false);
  const [plumePanelPosition, setPlumePanelPosition] = useState(null);

  const { complaints, fetchComplaints } = useComplaints();
  const { incidents, fetchIncidents } = useIncidents();

  const [selectedEmissionPointId, setSelectedEmissionPointId] = useState(null);

  const [plumeMode, setPlumeMode] = useState(false);

  useEffect(() => {
    if (!canAccessHistoricalTab && location.pathname === '/readings') {
      navigate('/complaints');
    }
  }, [location.pathname, hasRole, navigate]);

  const activeView = getActiveView(location.pathname);

  const handleShowDeviceInfo = (device) => {
    setSelectedComplaint(null);
    setComplaintInfoOpen(false);
    setSelectedDevice(device);
    setDeviceInfoOpen(!!device);
    setSelectedIncident(null);
    setIncidentInfoOpen(false);
  };

  const handleSelectDevice = (device) => {
    if (location.pathname === '/map') {
      if (device) {
        setSelectedDeviceId(device.id);
        flyToDevice(device);
        setDeviceInfoOpen(true);
      } else {
        setSelectedDeviceId(null);
      }
    }
  };

  const flyToDevice = (device) => {
    if (mapRef.current && mapRef.current.flyToMarker) {
      mapRef.current.flyToMarker(device.latitude, device.longitude, device.id);
    }
  };

  useEffect(() => {
    if (location.pathname === '/map' && pendingFlyTo && mapReady && mapRef.current?.flyToMarker) {
      const { lat, lng, id } = pendingFlyTo;
      mapRef.current.flyToMarker(lat, lng, id);
      setPendingFlyTo(null);
    }
  }, [location.pathname, pendingFlyTo, mapReady]);

  useEffect(() => {
    if (location.pathname === '/map' && mapRef.current?.centerMarkers) {
      mapRef.current.centerMarkers();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/map') {
      fetchComplaints();
      fetchIncidents();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/map') {
      // Device
      setDeviceInfoOpen(false);
      setSelectedDevice(null);

      // Complaints
      setSelectedComplaint(null);
      setComplaintInfoOpen(false);

      // Incidents
      setSelectedIncident(null);
      setIncidentInfoOpen(false);

      // Plume
      setPlumePanelVisible(false);
      setPlumePanelCollapsed(false);
      setPlumePanelPosition(null);
      disablePlumeMode();

      if (mapRef.current?.cleanUpPlume) {
        mapRef.current.cleanUpPlume();
      }
    }
  }, [location.pathname]);

  const forceOpenPanel = id =>
    setOpenPanels(panels => (panels.includes(id) ? panels : [...panels, id]));

  const handleShowComplaint = (complaint) => {
    setSelectedDevice(null);
    setDeviceInfoOpen(false);
    setSelectedComplaint(complaint);
    setComplaintInfoOpen(!!complaint);
    setSelectedIncident(null);
    setIncidentInfoOpen(false);
  };

  const handleComplaintResolve = async (id, form) => {
    try {
      startLoading();
      await complaintsService.resolve(id, form.getFieldValue('observations'));
      message.success('Complaint marked as resolved');
      setSelectedComplaint(null);
      fetchComplaints();
    } catch {
      message.error('Failed to mark as resolved');
    } finally {
      stopLoading();
    }
  };

  const handleComplaintReopen = async (id) => {
    try {
      startLoading();
      await complaintsService.reopen(id);
      message.success('Complaint reopened');
      setSelectedComplaint(null);
      fetchComplaints();
    } catch {
      message.error('Failed to reopen complaint');
    } finally {
      stopLoading();
    }
  };

  const handleComplaintUpdate = async (id, updatedData) => {
    try {
      startLoading();
      setEditing(true);
      await complaintsService.update(id, updatedData);
      message.success('Complaint updated successfully');
      setSelectedComplaint(null);
      fetchComplaints();
    } catch (err) {
      setEditing(false);
      message.error('Failed to update complaint');
    } finally {
      stopLoading();
    }
  };

  const handleShowIncident = (incident) => {
    setSelectedDevice(null);
    setDeviceInfoOpen(false);
    setSelectedComplaint(null);
    setComplaintInfoOpen(false);
    setSelectedIncident(incident);
    setIncidentInfoOpen(!!incident);
  };

  const handleIncidentResolve = async (id, form) => {
    try {
      startLoading();
      await incidentsService.resolve(id, form.getFieldValue('observations'));
      message.success('Incident marked as resolved');
      setSelectedIncident(null);
      fetchIncidents();
    } catch {
      message.error('Failed to mark as resolved');
    } finally {
      stopLoading();
    }
  };

  const handleIncidentReopen = async (id) => {
    try {
      startLoading();
      await incidentsService.reopen(id);
      message.success('Incident reopened');
      setSelectedIncident(null);
      fetchIncidents();
    } catch {
      message.error('Failed to reopen incident');
    } finally {
      stopLoading();
    }
  };

  const handleIncidentUpdate = async (id, updatedData) => {
    try {
      startLoading();
      setEditing(true);
      await incidentsService.update(id, updatedData);
      message.success('Incident updated successfully');
      setSelectedIncident(null);
      fetchIncidents();
    } catch (err) {
      setEditing(false);
      message.error('Failed to update incident');
    } finally {
      stopLoading();
    }
  };

  const handleOpenPlumePanel = (position = null) => {
    setPlumePanelPosition(position);
    setPlumePanelVisible(true);
    setPlumePanelCollapsed(true);
  };

  const handleClosePlumePanel = () => {
    setPlumePanelVisible(false);
    setPlumePanelPosition(null);
    setPlumePanelCollapsed(false);
    if (mapRef.current?.cleanUpPlume) {
      mapRef.current.cleanUpPlume();
    }
    setSelectedEmissionPointId(null);
    setPlumeMode(false);
  };

  const togglePlumeMode = () => {
    if (plumeMode) {
      setPlumeMode(false);
      setPlumePanelVisible(false);
      setPlumePanelCollapsed(false);

      if (mapRef.current?.cleanUpPlume) {
        mapRef.current.cleanUpPlume({ keepEmissionPoints: false });
      }

      setSelectedEmissionPointId(null);
      return;
    }

    setPlumeMode(true);
    setPlumePanelVisible(true);
    setPlumePanelCollapsed(true);
  };

  const disablePlumeMode = () => {
    setPlumeMode(false);
    setPlumePanelVisible(false);
    setPlumePanelCollapsed(false);

    if (mapRef.current?.cleanUpPlume) {
      mapRef.current.cleanUpPlume({ keepEmissionPoints: false });
    }

    setSelectedEmissionPointId(null);
  };

  return (
    <>
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{ height: 64, lineHeight: '64px', padding: 0, zIndex: 1201 }}>
          <TopBar activeView={activeView} />
        </Header>
        <div style={{ display: 'flex', flexDirection: 'row', height: 'calc(100vh - 64px)', minHeight: 0, overflowX: 'hidden' }}>
          {canViewSidebar && (
            <Sidebar
              onSelectDevice={handleSelectDevice}
              selectedDeviceId={selectedDeviceId}
              onShowInfo={handleShowDeviceInfo}
              flyToDevice={flyToDevice}
              openPanels={openPanels}
              setOpenPanels={setOpenPanels}
              forceOpenPanel={forceOpenPanel}
            />
          )}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#fff' }}>
            <div style={{ flex: 1, minHeight: 0 }}>
              <Outlet context={{
                mapRef,
                onReady: () => setMapReady(true),
                onShowInfo: handleShowDeviceInfo,
                onSelectDevice: handleSelectDevice,
                forceOpenPanel,
                onMapMove: setMapCenter,
                selectedComplaint,
                setSelectedComplaint,
                onShowComplaint: handleShowComplaint,
                complaints,
                fetchComplaints,
                handleOpenPlumePanel,
                plumePanelVisible,
                plumePanelPosition,
                plumePanelCollapsed,
                handleClosePlumePanel,
                setPlumePanelCollapsed,
                incidents,
                fetchIncidents,
                selectedIncident,
                setSelectedIncident,
                onShowIncident: handleShowIncident,
                selectedEmissionPointId,
                setSelectedEmissionPointId,
                setPlumePanelCollapsed,
                plumeMode,
                togglePlumeMode,
                disablePlumeMode,
              }} />
            </div>
          </div>
        </div>
        <DeviceMapPanel
          device={selectedDevice}
          open={deviceInfoOpen && location.pathname === '/map'}
          onClose={() => handleShowDeviceInfo(null)}
        />
        {selectedComplaint && (
          <ComplaintMapPanel
            complaint={selectedComplaint}
            onClose={() => setSelectedComplaint(null)}
            onResolve={handleComplaintResolve}
            onReopen={handleComplaintReopen}
            onUpdate={handleComplaintUpdate}
            onRefresh={fetchComplaints}
            readOnly={hasRole('COMPLAINTS_VIEW')}
            canViewSidebar={canViewSidebar}
            open={selectedComplaint && location.pathname === '/map'}
          />
        )}
        {selectedIncident && (
          <IncidentMapPanel
            incident={selectedIncident}
            onClose={() => setSelectedIncident(null)}
            onResolve={handleIncidentResolve}
            onReopen={handleIncidentReopen}
            onUpdate={handleIncidentUpdate}
            onRefresh={fetchIncidents}
            readOnly={hasRole('INCIDENTS_VIEW')}
            canViewSidebar={canViewSidebar}
            open={selectedIncident && location.pathname === '/map'}
          />
        )}
        <PlumeMapPanel
          visible={plumePanelVisible && location.pathname === '/map'}
          position={plumePanelPosition}
          collapsed={plumePanelCollapsed}
          onClose={handleClosePlumePanel}
          onToggleCollapse={() => setPlumePanelCollapsed(v => !v)}
          selectedEmissionPointId={selectedEmissionPointId}
          setSelectedEmissionPointId={setSelectedEmissionPointId}
          onSelectPoint={(lat, lng, name) => {
            if (mapRef.current?.flyToMarker) {
              mapRef.current.flyToMarker(lat, lng, null);
            }
            if (mapRef.current?.setEmissionMarker) {
              mapRef.current.setEmissionMarker(lat, lng, name);
            }
            if (mapRef.current?.setClickPosition) {
              mapRef.current.setClickPosition({ lat, lng });
            }
          }}
          onSubmit={(values) => {
            if (mapRef.current?.handlePlumeSubmit) {
              mapRef.current.handlePlumeSubmit(values);
            }
          }}
        />
        {plumePanelVisible && location.pathname === '/map' && (
          <button
            onClick={() => setPlumePanelCollapsed(v => !v)}
            style={{
              position: 'absolute',
              top: 74,
              right: 0,  // <-- siempre 0, ahora lo movemos con transform
              transform: plumePanelCollapsed
                ? 'translateX(0)'        // botón pegado al borde derecho cuando está colapsado
                : 'translateX(-400px)',   // botón se desplaza hacia la izquierda cuando el panel está visible
              transition: 'transform 0.35s cubic-bezier(.25,.8,.25,1)', // animación premium
              zIndex: 1000,
              backgroundColor: '#0075C9',
              color: 'white',
              border: 'none',
              borderRadius: '4px 0 0 4px',
              width: 24,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
              transition: 'right 0.3s ease-in-out',
              cursor: 'pointer'
            }}
            title={plumePanelCollapsed ? "Show plume panel" : "Hide plume panel"}
          >
            {plumePanelCollapsed ? '❮' : '❯'}
          </button>
        )}
      </Layout>
    </>
  );
}

export default Dashboard;
